import { HexColor, IconType } from "./enums.js";

export const GLOBAL_ORG_NAME = "Global";

export const ORG_DISPLAY_NAME_REGEX = "^[A-Za-z0-9'_ -]+$";

/** Allowed characters when creating org IDs and user-facing IDs. */
export const ORG_IDENTIFIER_REGEX = /^[a-z0-9-]+$/;

/** Existing org IDs and handles may contain underscores and must remain routable. */
export const ORG_URL_SEGMENT_REGEX = /^[a-z0-9_-]+$/;

/**
 * Length bounds for a `userFacingId`. The max mirrors the DNS label limit so a
 * handle can safely appear in a hostname if we ever subdomain workspaces; the
 * min blocks trivially short, ambiguous handles. These apply ONLY to the
 * customer-chosen `userFacingId`, not the canonical `id` — legacy org ids are
 * unbounded (e.g. long test/system-generated slugs), so {@link
 * validateOrgIdentifierFormat} deliberately does not enforce them.
 */
export const ORG_USER_FACING_ID_MIN_LENGTH = 3;
export const ORG_USER_FACING_ID_MAX_LENGTH = 63;

/**
 * Validate the base format of a new org identifier (`id` or `userFacingId`):
 * non-empty and drawn from the shared character set. Returns a human-readable
 * error string, or `null` if valid. Does NOT check reservation ({@link
 * RESERVED_ORG_IDS}) or uniqueness — callers layer those on top. For the
 * customer-chosen handle, use {@link validateOrgUserFacingIdFormat}, which adds
 * length and separator constraints on top of this.
 */
export const validateOrgIdentifierFormat = (
  identifier: string,
): string | null => {
  if (identifier.length === 0) {
    return "must not be empty";
  }
  if (!ORG_IDENTIFIER_REGEX.test(identifier)) {
    return "may only contain lowercase letters, digits, and '-'";
  }
  return null;
};

/**
 * Validate a customer-chosen `userFacingId`. Layers length bounds and a
 * leading/trailing-separator guard on top of {@link validateOrgIdentifierFormat}
 * so the set-once handle produces a clean, unambiguous URL segment. Not applied
 * to the canonical `id`, which keeps the looser base rules.
 */
export const validateOrgUserFacingIdFormat = (
  userFacingId: string,
): string | null => {
  const baseError = validateOrgIdentifierFormat(userFacingId);
  if (baseError != null) {
    return baseError;
  }
  if (userFacingId.length < ORG_USER_FACING_ID_MIN_LENGTH) {
    return `must be at least ${ORG_USER_FACING_ID_MIN_LENGTH} characters`;
  }
  if (userFacingId.length > ORG_USER_FACING_ID_MAX_LENGTH) {
    return `must be at most ${ORG_USER_FACING_ID_MAX_LENGTH} characters`;
  }
  // Reject leading/trailing separators (and separator-only values like `---`)
  // so handles produce clean, unambiguous URL segments.
  if (!/^[a-z0-9]/.test(userFacingId) || !/[a-z0-9]$/.test(userFacingId)) {
    return "must start and end with a letter or digit";
  }
  return null;
};

const POST_ORG_PICKER_REDIRECT_ROUTES = ["new-project", "new-thread"];

/**
/**
 * TODO(VELO-4968):revisit this if we broaden redirect
 * support.
 * Checks to see if redirectTo is a valid post org picker redirect route -> we
 * only want to redirect to this route after landing a user in an specific org.
 * Workspace picker will redirect to /:orgId/:redirectTo
 */
export const isKnownPostOrgPickerRedirect = (redirectTo: string): boolean => {
  const baseRoute = redirectTo?.split("?")[0]?.split("/")[1];

  if (!baseRoute || !POST_ORG_PICKER_REDIRECT_ROUTES.includes(baseRoute)) {
    return false;
  }
  return true;
};

// Org IDs that we may need for other parts of our URL
export const RESERVED_ORG_IDS = [
  "new-project",
  "new-thread",
  "storage",
  "auth",
  "auth-all",
  "home",
  "app",
  "admin",
  "account",
  "admin-platform",
  "terms",
  "terms-and-conditions",
  "privacy-policy",
  "project",
  "profile",
  "billing",
  "cli",
  "compute",
  "security",
  "static",
  "build",
  "upgrade",
  "org",
  "orgs",
  "search",
  "new",
  "api",
  "internal-api",
  "v0",
  "v1",
  "v2",
  "v3",
  "v4",
  "v5",
  "v6",
  "settings",
  "invite",
  "mcp",
  "member",
  "members",
  "user",
  "users",
  "team",
  "teams",
  "redirect",
  "usage",
  "stats",
  "insights",
  "data",
  "graphql",
  "subscriptions",
  "dist",
  "export",
  "oauth",
  "org-redirect",
  "org-selector",
  "healthcheck",
  "public",
  "queues",
  "login",
  "login-success",
  "log-in",
  "logout",
  "log-out",
  "logout-all",
  "log-out-all",
  "signin",
  "sign-in",
  "signup",
  "sign-up",
  "signout",
  "sign-out",
  "spellbook",
  "hooks",
  "notion-unfurl",
  "create-customer-portal-session",
  "create-checkout-session",
  "bigquery-oauth-success",
  "snowflake-oauth-success",
  "databricks-oauth-success",
  "welcome",
  // todo(ENG-3717) - move these four GH authe routes to use nested /github/... routes
  "github-connect-success",
  "github-install-landing",
  "githubsync-connect-success",
  "githubsync-install-landing",
  "gitlabsync-install-landing",
  "slack-auth",
  "app-icon",
  "favicon",
  "zd-jwt",
  "collection",
  "collections",
  "component",
  "components",
  "quick",
  "new-quick",
  "workspace-picker",
  "link",
  "new-trial",
  "google-drive-oauth-complete",
  "mcp-server-oauth-result",
  "verify-org-domain",
  "email-unsubscribe",
];

export const DEFAULT_CATEGORIES = [
  {
    color: HexColor.RED,
    name: "Deep dive",
    description:
      "A one-off analysis, intended to be read linearly as a written up document, with results that reflect a point in time",
  },
  {
    color: HexColor.ORANGE,
    name: "Dashboard",
    description: "Dashboard that shows recent results",
  },
  {
    color: HexColor.BLUE,
    name: "Feature engineering",
    description: "Project for prototyping, training, and testing ML models",
  },
  {
    color: HexColor.GRAY,
    name: "Scratchpad",
    description:
      "Personal scratchpad for a team member. Not intended for broad consumption",
  },
  {
    color: HexColor.PURPLE,
    name: "Template",
    description:
      "Template to be duplicated and used as a starting point for additional analyses",
  },
  {
    color: HexColor.TEAL,
    name: "Product",
    description: null,
  },
  {
    color: HexColor.GREEN,
    name: "Sales",
    description: null,
  },
  {
    color: HexColor.TEAL,
    name: "Finance",
    description: null,
  },
  {
    color: HexColor.RED,
    name: "Ops",
    description: null,
  },
  {
    color: HexColor.PURPLE,
    name: "Marketing",
    description: null,
  },
  {
    color: HexColor.YELLOW,
    name: "External",
    description: null,
  },
];

export const DEFAULT_STATUSES = [
  {
    color: HexColor.YELLOW,
    name: "In development",
    description: "Currently in progress, not yet ready for use",
    endorsed: false,
    enforcesReview: false,
    icon: null,
  },
  {
    color: HexColor.BLUE,
    name: "Completed",
    description: "Completed and ready for use",
    endorsed: false,
    enforcesReview: false,
    icon: null,
  },
  {
    color: HexColor.GREEN,
    name: "Endorsed",
    description: "Vetted by the data team",
    endorsed: true,
    enforcesReview: true,
    icon: IconType.certified,
  },
];

export const MAX_TRIAL_AGENT_CREDITS = 2000; // $2000 max, discretionary for sales
